import axios from 'axios';

let baseURL = '/api'; // 生产环境使用相对路径
let imageBaseURL = window.location.origin; // 生产环境图片基础URL

if (process.env.NODE_ENV === 'development') {
    baseURL = 'http://localhost:5000/api'; // 开发环境直接连接后端
    imageBaseURL = 'http://localhost:5000'; // 开发环境图片基础URL
}


const apiClient = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// 请求拦截器：自动注入管理员信息到请求 body
apiClient.interceptors.request.use(config => {
    try {
        const adminInfo = JSON.parse(localStorage.getItem('admin_login_info'));
        if (adminInfo && adminInfo.id) {
            if (config.method === 'post' || config.method === 'put') {
                if (!config.data) {
                    config.data = {};
                }
                if (typeof config.data === 'object' && config.data !== null) {
                    config.data.admin_id = adminInfo.id;
                    config.data.admin_name = adminInfo.realname || adminInfo.username || '';
                }
                if (typeof config.data === 'string') {
                    try {
                        const parsed = JSON.parse(config.data);
                        parsed.admin_id = adminInfo.id;
                        parsed.admin_name = adminInfo.realname || adminInfo.username || '';
                        config.data = JSON.stringify(parsed);
                    } catch (e) { /* ignore */ }
                }
            }
        }
    } catch (e) { /* ignore */ }
    return config;
}, error => {
    return Promise.reject(error);
});

// 图片URL处理工具方法
const getFullImageUrl = (imageUrl, useThumbnail = 'small') => {
    // 如果是对象，提取fileUrl属性
    if (imageUrl && typeof imageUrl === 'object') {
        imageUrl = imageUrl.fileUrl || imageUrl.url || '';
    }
    
    // 如果不是字符串或为空，返回默认图片
    if (!imageUrl || typeof imageUrl !== 'string') {
        return `${imageBaseURL}/uploads/nopic.png`;
    }
    
    // 如果已经是完整URL，提取相对路径用于缩略图处理
    let relativePath = imageUrl;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        // 提取 /uploads/xxx.jpg 部分
        const uploadsIndex = imageUrl.indexOf('/uploads/');
        if (uploadsIndex !== -1) {
            relativePath = imageUrl.substring(uploadsIndex);
        } else {
            // 如果不包含 /uploads/，直接返回原URL
            return imageUrl;
        }
    }
    
    // 处理缩略图
    if (useThumbnail && relativePath.startsWith('/uploads/')) {
        // 如果不是 thumbnails 目录，添加缩略图路径
        if (!relativePath.includes('/thumbnails/')) {
            const filename = relativePath.replace('/uploads/', '');
            const thumbnailUrl = `/uploads/thumbnails/${useThumbnail}_${filename}`;
            return `${imageBaseURL}${thumbnailUrl}`;
        }
        // 如果已经是缩略图路径，直接拼接
        return `${imageBaseURL}${relativePath}`;
    }
    
    // 返回完整URL
    return `${imageBaseURL}${relativePath}`;
};

apiClient.interceptors.response.use(
    response => {
        return response;
    },
    error => {
        if (error.response) {
            console.error('服务器响应错误:', error.response.data);
            console.error('状态码:', error.response.status);
            console.error('响应头:', error.response.headers);
        } else if (error.request) {
            console.error('没有收到响应:', error.request);
        } else {
            console.error('发生错误:', error.message);
        }
        return Promise.reject(error);
    }
);

const API = {  
    userLogin: async (postData) => {
        return await apiClient.post('/userlogin', postData);
    },
    userLoginByPhone: async (postData) => {
        return await apiClient.post('/userloginbyphone', postData);
    },
    setPassword: async (postData) => {
        return await apiClient.post('/setpassword', postData);
    },
    resetUserPassword: async (postData) => {
        return await apiClient.post('/resetuserpassword', postData);
    },
    adminLogin: async (postData) => {
        return await apiClient.post('/adminlogin', postData);
    },
    checkAdminStatus: async (postData) => {
        return await apiClient.post('/checkadminstatus', postData);
    },
    setAdminPassword: async (postData) => {
        return await apiClient.post('/setadminpassword', postData);
    },
    getUserInfo: async (id) => {
        return await apiClient.post('/getuserinfo', { 'userId': id });
    },
    getNewMenu: async (postData) => {
        return await apiClient.post('/getnewmenu', postData);
    },
    applyOrder: async (postData) => {
        return await apiClient.post('/applyorder', { 'postData': postData });
    },
    updateOrder: async (postData) => {
        return await apiClient.post('/updateorder', postData)
    },
    checkOrderStatus: (id) => {
        return apiClient.post('/checkorderstatus', { id: id })
    },
    getOrderList: async (postData) => {
        return await apiClient.post('/getorderlist', postData);
    },
    getExportOrder: async (postData) => {
        return await apiClient.post('/getexportorder', postData);
    },
    getNewOrderList: async (postData) => {
        return await apiClient.post('/getneworderlist', postData);
    },
    addUser: async (postData) => {
        return await apiClient.post('/adduser', postData);
    },
    updateBalance: async (postData) => {
        return await apiClient.post('/updatebalance', postData);
    },
    updatePoint: async (postData) => {
        return await apiClient.post('/updatepoint', postData);
    },
    addRechargeRecord: async (postData) => {
        return await apiClient.post('/addrechargerecord', postData);
    },
    addPointRecord: async (postData) => {
        return await apiClient.post('/addpointrecord', postData);
    },
    getShopsByArea: async (postData) => {
        return await apiClient.post('/getshopsbyarea', postData);
    },
    // ...管理员API方法   
    getDataBoards: async () => {
        return await apiClient.get('/getdataboards');
    },
    getGlobalDataBoard: async (scope) => {
        const config = scope ? { params: { scope } } : {};
        return await apiClient.get('/global-data-board', config);
    },
    getUserList: async (postData) => {
        return await apiClient.post('/userlist', postData);
    },
    updateUser: async (postData) => {
        return await apiClient.post('/updateuser', postData);
    },
    deleteUser: async (postData) => {
        return await apiClient.post('/deleteuser', postData);
    },
    updateMenu: async (postData) => {
        return await apiClient.post('/updatemenu', postData);
    },
    getMenus: async (postData) => {
        return await apiClient.post('/getmenus', postData);
    },
    getActiveMenus: async (postData) => {
        return await apiClient.post('/get-active-menus', postData);
    },
    delFromDishList: async (postData) => {
        return await apiClient.post('/delfromdishlist', postData);
    },
    addToDishs: async (postData) => {
        return await apiClient.post('/addtodishs', postData);
    },
    updateDish: async (postData) => {
        return await apiClient.post('/updatedish', postData);
    },
    getDishs: async (postData) => {
        return await apiClient.post('/getdishs', postData);
    },
    getLastMenu: async () => {
        return await apiClient.get('/getlastmenu');
    },
    addMenu: async (postData) => {
        return await apiClient.post('/addmenu', postData);
    },
    batchPublishMenu: async (postData) => {
        return await apiClient.post('/batch-publish-menu', postData);
    },
    getMenuStatus: async (postData) => {
        return await apiClient.post('/getmenustatus', postData);
    },
    stopAllMenus: async () => {
        return await apiClient.post('/stopallmenus');
    },
    // ...超级管理员后台API方法   
    addArea: async (postData) => {
        return await apiClient.post('/addarea', postData);
    },
    updateArea: async (postData) => {
        return await apiClient.post('/updatearea', postData);
    },
    getAreas: async (postData) => {
        return await apiClient.post('/getareas', postData);
    },
    getButlerOptions: async () => {
        return await apiClient.get('/butler/options');
    },
    getOptions: async (postData) => {
        return await apiClient.post('/getoptions', postData);
    },
    addShopType: async (postData) => {
        return await apiClient.post('/addshoptype', postData);
    },
    updateShopType: async (postData) => {
        return await apiClient.post('/updateshoptype', postData);
    },
    deleteShopType: async (postData) => {
        return await apiClient.post('/deleteshoptype', postData);
    },
    getShopTypes: async (postData) => {
        return await apiClient.post('/getshoptypes', postData);
    },
    addShop: async (postData) => {
        return await apiClient.post('/addshop', postData);
    },
    getShops: async (postData) => {
        return await apiClient.post('/getshops', postData);
    },
    updateShop: async (postData) => {
        return await apiClient.post('/updateshop', postData);
    },
    updateShopSettings: async (postData) => {
        return await apiClient.post('/updateshopsettings', postData);
    },
    updateUsersArea: async (postData) => {
        return await apiClient.post('/updateusersarea', postData);
    },
    getRechargeList: async (postData) => {
        return await apiClient.post('/getrechargelist', postData);
    },
    balanceTransactions: async (postData) => {
        return await apiClient.post('/balance-transactions', postData);
    },
    balanceTransactionTypes: async () => {
        return await apiClient.get('/balance-transaction-types');
    },
    addCategory: async (postData) => {
        return await apiClient.post('/addcategory', postData);
    },
    deleteCategory: async (postData) => {
        return await apiClient.post('/deletecategory', postData);
    },
    getCategories: async (postData) => {
        return await apiClient.post('/getcategories', postData);
    },
    updateCategory: async (postData) => {
        return await apiClient.post('/updatecategory', postData);
    },
    addMemberLevel: async (postData) => {
        return await apiClient.post('/addmemberlevel', postData);
    },
    editMemberLevel: async (postData) => {
        return await apiClient.post('/editmemberlevel', postData);
    },
    getMemberLevels: async (postData) => {
        return await apiClient.post('/getmemberlevels', postData);
    },
    
    // 获取图表数据
    getChartData: async () => {
        return await apiClient.get('/getchartdata');
    },
    addTaocan: async (postData) => {
        return await apiClient.post('/addtaocan', postData);
    },
    getTaocans: async (postData) => {
        return await apiClient.post('/gettaocans', postData);
    },
    delTaocan: async (postData) => {
        return await apiClient.post('/deltaocan', postData);
    },
    addShopPsArea: async (postData) => {
        return await apiClient.post('/addshoppsarea', postData);
    },
    updateShopPsArea: async (postData) => {
        return await apiClient.post('/updateshoppsarea', postData);
    },
    checkShopPsArea: async (postData) => {
        return await apiClient.post('/checkshoppsarea', postData);
    },
    getAdminList: async (postData) => {
        return await apiClient.post('/getadminlist', postData);
    },
    addAdmin: async (postData) => {
        return await apiClient.post('/addadmin', postData);
    },
    updateAdmin: async (postData) => {
        return await apiClient.post('/updateadmin', postData);
    },
    delAdmin: async (postData) => {
        return await apiClient.post('/deladmin', postData);
    },
    updateUserFamily: async (postData) => {
        return await apiClient.post('/updateuserfamily', postData);
    },
    updateRechargeStatus: async (postData) => {
        return await apiClient.post('/updaterechargestatus', postData);
    },
    getUserListByIds: async (postData) => {
        return await apiClient.post('/getuserlistbyids', postData);
    },
    setPrinter: async (postData) => {
        return await apiClient.post('/setprinter', postData);
    },
    getPrinter: async (postData) => {
        return await apiClient.post('/getprinter', postData);
    },
    getPrintersStatus: async (postData) => {
        return await apiClient.post('/getprintersstatus', postData);
    },
    getOrdersPrintStatus: async (postData) => {
        return await apiClient.post('/getordersprintstatus', postData);
    },
    printOrder: async (postData) => {
        return await apiClient.post('/printorder', postData);
    },
    printCancelOrder: async (postData) => {
        return await apiClient.post('/printcancelorder', postData);
    },
    setShopRight: async (postData) => {
        return await apiClient.post('/setshopright', postData);
    },
    getShopRight: async (postData) => {
        return await apiClient.post('/getshopright', postData);
    },
    getShopIds: async (postData) => {
        return await apiClient.post('/getshopids', postData);
    },
    getFinance: async (postData) => {
        return await apiClient.post('/getfinance', postData);
    },
    getShopsByIds:async (postData) => {
        return await apiClient.post('/getshopsbyids', postData);
    },
    updateOrdersStatus: async (postData) => {
        return await apiClient.post('/updateordersstatus', postData);
    },
    deliverAllOrders: async (postData) => {
        return await apiClient.post('/deliver-all-orders', postData);
    },
    printRechargeOrder:async (postData) => {
        return await apiClient.post('/printrechargeorder', postData);
    },
    addImageCategory: async (postData) => {
        return await apiClient.post('/addimagecategory', postData);
    },
    getImageCategories: async (postData) => {
        return await apiClient.get('/getimagecategories', postData);
    },
    // 小程序订阅消息模板配置（后台维护模板ID/跳转页面/关键词映射）
    getWxSubscribeTemplates: async () => {
        return await apiClient.get('/wx-subscribe-templates');
    },
    saveWxSubscribeTemplate: async (postData) => {
        return await apiClient.post('/wx-subscribe-templates', postData);
    },
    // 订阅消息发送记录（分页 + 查询）
    getWxSubscribeLogs: (params) => apiClient.get('/wx-subscribe-logs', { params }),
    // 公众号消息推送配置（账号信息 / 模板配置 / 扫码绑定）
    getMpAccountConfig: () => apiClient.get('/mp-account-config'),
    saveMpAccountConfig: (postData) => apiClient.post('/mp-account-config', postData),
    getMpTemplateConfigs: () => apiClient.get('/mp-template-configs'),
    saveMpTemplateConfig: (postData) => apiClient.post('/mp-template-config', postData),
    createMpBindQrcode: (postData) => apiClient.post('/mp-bind-qrcode', postData),
    getMpBindStatus: (token) => apiClient.get('/mp-bind-status?token=' + encodeURIComponent(token)),
    // 公众号推送记录（分页 + 查询）
    getMpPushLogs: (params) => apiClient.get('/mp-push-logs', { params }),
    updateImageCategory: async (postData) => {
        return await apiClient.post('/updateimagecategory', postData);
    },
    delImageCategory: async (postData) => {
        return await apiClient.post('/delimagecategory', postData);
    },
    getImages:async (postData) => {
        return await apiClient.post('/getimages', postData);
    },
    delImage: async (postData) => {
        return await apiClient.post('/delimage', postData);
    },
    delImages: async (postData) => {
        return await apiClient.post('/delimages', postData);
    },
    addProductCategory:async(postData)=>{
        return await apiClient.post('/addproductcategory', postData);
    },
    addProduct:async(postData)=>{
        return await apiClient.post('/addproduct', postData);
    },   
    getProductDetail:async(postData)=>{
        return await apiClient.post('/getproductdetail', postData);
    },
    delProductCategory:async(postData)=>{
        return await apiClient.post('/deleteproductcategory', postData);
    },
    getProductCategories:async(postData)=>{
        return await apiClient.post('/getproductcategories', postData);
    },
    updateProductCategory:async(postData)=>{
        return await apiClient.post('/updateproductcategory', postData);
    },
    updateProductCategorySort:async(postData)=>{
        return await apiClient.post('/updateproductcategorysort', postData);
    },
    updateProductCategoryStatus:async(postData)=>{
        return await apiClient.post('/updateproductcategorystatus', postData);
    },
    getProducts:async(postData)=>{
        return await apiClient.post('/getproducts', postData);
    },
    getCartProducts:async(postData)=>{
        return await apiClient.post('/getcartproducts',postData)
    },
    getProductsByIds:async(productIds)=>{
        return await apiClient.post('/getproductsbyids', { ids: productIds });
    },
    updateProduct:async(postData)=>{
        return await apiClient.post('/updateproduct', postData);
    },
    delProduct:async(postData)=>{
        return await apiClient.post('/deleteproduct', postData);
    },
    batchDeleteProducts:async(postData)=>{
        return await apiClient.post('/batchdeleteproducts', postData);
    }, 
    copyProduct:async(postData)=>{
        return await apiClient.post('/copyproduct', postData);
    },
    batchUpdateProductShop:async(postData)=>{
        return await apiClient.post('/batchupdateproductshop', postData);
    },
    batchUpdateProductCategory:async(postData)=>{
        return await apiClient.post('/batchupdateproductcategory', postData);
    },
    batchUpdateProductStatus:async(postData)=>{
        return await apiClient.post('/batchupdateproductstatus', postData);
    },
    batchUpdateProductRecommend:async(postData)=>{
        return await apiClient.post('/batchupdateproductrecommend', postData);
    },
    updateProductSort:async(postData)=>{
        return await apiClient.post('/updateproductsort', postData);
    },

    // 商城订单列表（后台管理）
    getMallOrders: async (params) => {
        return await apiClient.get('/mallorders', { params });
    },

    // 商城订单状态统计（各状态下订单数量）
    getMallOrdersStats: async (params) => {
        return await apiClient.get('/mallorders/stats', { params });
    },

    // 按商品筛选订单：搜索商品（供商品选择弹窗勾选具体商品）
    getMallOrderProductOptions: async (params) => {
        return await apiClient.get('/mallorders/product-options', { params });
    },

    // 导出商城订单（可选择列）
    exportMallOrders: async (params) => {
        return await apiClient.get('/mallorders/export', { params, responseType: 'blob' });
    },

    // 关闭商城订单（后台管理，仅待支付订单：0 -> 已关闭4）
    cancelMallOrder: async (postData) => {
        return await apiClient.post('/cancelmallorder', postData);
    },

    // 批量删除商城订单（仅已关闭订单可删除）
    batchDeleteMallOrders: async (orderIds) => {
        return await apiClient.post('/batchdeletemallorders', { order_ids: orderIds });
    },

    // 后台发货：代发货 -> 待收货
    shipMallOrder: async (postData) => {
        return await apiClient.post('/shipmallorder', postData);
    },

    // 后台确认收货：待收货 -> 已完成
    markMallReceived: async (postData) => {
        return await apiClient.post('/markmallreceived', postData);
    },

    // 后台标记退款/售后：代发货/待收货 -> 退款/售后（立即退款）
    markMallRefund: async (postData) => {
        return await apiClient.post('/markmallrefund', postData);
    },

    // 后台审核退款：退款/售后状态订单按原支付方式退款（微信支付原路退回微信，余额支付退回余额）
    refundMallOrder: async (postData) => {
        return await apiClient.post('/refundmallorder', postData);
    },

    // 后台订单「确认退款」：处理退款/售后订单（含仅退款售后申请），退款后售后单同步置为已退款
    confirmMallRefund: async (postData) => {
        return await apiClient.post('/mallorders/confirm-refund', postData);
    },

    // 售后申请列表（后台管理，GET）
    getMallAfterSales: async (params) => {
        return await apiClient.get('/mallaftersales', { params });
    },

    // 审核售后申请（approve 通过 / reject 驳回）
    auditMallAfterSale: async (postData) => {
        return await apiClient.post('/auditmallaftersale', postData);
    },

    // 物流轨迹查询（快递100）：传 { com: 快递公司编码, num: 快递单号 }
    queryKuaidiTrack: async (postData) => {
        return await apiClient.post('/kuaiditrack', postData);
    },

    // 系统参数管理（text/json/bool）
    getSystemParamList: async (postData) => {
        return await apiClient.post('/systemparam/list', postData);
    },
    addSystemParam: async (postData) => {
        return await apiClient.post('/systemparam/create', postData);
    },
    updateSystemParam: async (postData) => {
        return await apiClient.post('/systemparam/update', postData);
    },
    deleteSystemParam: async (postData) => {
        return await apiClient.post('/systemparam/delete', postData);
    },

    // 商品评价管理（含内容安全「疑似违规」隐藏评价的人工放行）
    listMallReviews: async (params) => {
        return await apiClient.get('/mallreviews', { params });
    },
    updateMallReview: async (reviewId, postData) => {
        return await apiClient.put(`/mallreviews/${reviewId}`, postData);
    },
    removeMallReview: async (reviewId) => {
        return await apiClient.delete(`/mallreviews/${reviewId}`);
    },

    // 自提点设置
    listPickupPoints: async (params) => {
        return await apiClient.get('/pickuppoints', { params });
    },
    addPickupPoint: async (postData) => {
        return await apiClient.post('/pickuppoints', postData);
    },
    updatePickupPoint: async (pointId, postData) => {
        return await apiClient.put(`/pickuppoints/${pointId}`, postData);
    },
    removePickupPoint: async (pointId) => {
        return await apiClient.delete(`/pickuppoints/${pointId}`);
    },

    // 快递公司编码设置
    listCourierCompanies: async (params) => {
        return await apiClient.get('/couriercompanies', { params });
    },
    addCourierCompany: async (postData) => {
        return await apiClient.post('/couriercompanies', postData);
    },
    updateCourierCompany: async (companyId, postData) => {
        return await apiClient.put(`/couriercompanies/${companyId}`, postData);
    },
    removeCourierCompany: async (companyId) => {
        return await apiClient.delete(`/couriercompanies/${companyId}`);
    },

    // 退货地址管理（物流管理）
    listReturnAddresses: async (params) => {
        return await apiClient.get('/mallreturnaddresses', { params });
    },
    addReturnAddress: async (postData) => {
        return await apiClient.post('/mallreturnaddresses', postData);
    },
    updateReturnAddress: async (addressId, postData) => {
        return await apiClient.put(`/mallreturnaddresses/${addressId}`, postData);
    },
    removeReturnAddress: async (addressId) => {
        return await apiClient.delete(`/mallreturnaddresses/${addressId}`);
    },

    // 物流模板管理
    getLogisticsTemplates: async (postData) => {
        return await apiClient.post('/getlogistictemplates', postData);
    },
    addLogisticsTemplate: async (postData) => {
        return await apiClient.post('/addlogistictemplate', postData);
    },
    updateLogisticsTemplate: async (postData) => {
        return await apiClient.post('/updatelogistictemplate', postData);
    },
    deleteLogisticsTemplate: async (postData) => {
        return await apiClient.post('/deletelogistictemplate', postData);
    },

    // 获取全国行政区划（省市县三级）
    getNationalAreas: async (postData) => {
        return await apiClient.post('/getnationalareas', postData);
    },

    // 供应商管理
    getSuppliers: async (postData) => {
        return await apiClient.post('/getsuppliers', postData);
    },
    addSupplier: async (postData) => {
        return await apiClient.post('/addsupplier', postData);
    },
    updateSupplier: async (postData) => {
        return await apiClient.post('/updatesupplier', postData);
    },
    deleteSupplier: async (postData) => {
        return await apiClient.post('/deletesupplier', postData);
    },

    // ===== 商城营销管理 =====
    // 活动设置
    getMallActivities: async (postData) => {
        return await apiClient.post('/mallactivities', postData);
    },
    getMallActivityDetail: async (postData) => {
        return await apiClient.post('/mallactivity/detail', postData);
    },
    saveMallActivity: async (postData) => {
        return await apiClient.post('/mallactivity/save', postData);
    },
    deleteMallActivity: async (postData) => {
        return await apiClient.post('/mallactivity/delete', postData);
    },
    updateMallActivityStatus: async (postData) => {
        return await apiClient.post('/mallactivity/status', postData);
    },
    // 优惠设置（满减/打折/套餐）
    getMallDiscounts: async (postData) => {
        return await apiClient.post('/malldiscounts', postData);
    },
    getMallDiscountDetail: async (postData) => {
        return await apiClient.post('/malldiscount/detail', postData);
    },
    saveMallDiscount: async (postData) => {
        return await apiClient.post('/malldiscount/save', postData);
    },
    deleteMallDiscount: async (postData) => {
        return await apiClient.post('/malldiscount/delete', postData);
    },
    updateMallDiscountStatus: async (postData) => {
        return await apiClient.post('/malldiscount/status', postData);
    },
    
    // 文章分类管理
    getArticleCategories:async(postData)=>{
        return await apiClient.post('/getarticlecategories', postData);
    },
    addArticleCategory:async(postData)=>{
        return await apiClient.post('/addarticlecategory', postData);
    },
    updateArticleCategory:async(postData)=>{
        return await apiClient.post('/updatearticlecategory', postData);
    },
    deleteArticleCategory:async(postData)=>{
        return await apiClient.post('/deletearticlecategory', postData);
    },
    
    // 文章管理
    getArticles:async(postData)=>{
        return await apiClient.post('/getarticles', postData);
    },
    getArticleDetail:async(postData)=>{
        return await apiClient.post('/getarticledetail', postData);
    },
    addArticle:async(postData)=>{
        return await apiClient.post('/addarticle', postData);
    },
    updateArticle:async(postData)=>{
        return await apiClient.post('/updatearticle', postData);
    },
    deleteArticle:async(postData)=>{
        return await apiClient.post('/deletearticle', postData);
    },   
    updateArticleSort:async(postData)=>{
        return await apiClient.post('/updatearticlesort', postData);
    },
    updateArticleRecommend:async(postData)=>{
        return await apiClient.post('/updatearticlerecommend', postData);
    },       
   
    // 微信支付订单管理相关接口
    getWxPaymentOrders:async(postData)=>{
        return await apiClient.post('/getwxpaymentorders', postData);
    },    
    // 根据探访编号获取访问信息
    getInterviewInfoByNo: async(postData)=>{
        return await apiClient.post('/getinterviewinfobyno', postData);
    },   
    submitWxPayRefund:async(postData)=>{
        return await apiClient.post('/submitwxpayrefund', postData);
    },
    // 微信支付订单流转日志（分类/支付/退款审计追踪）
    getWxPayOrderLogs:async(postData)=>{
        return await apiClient.post('/getwxpayorderlogs', postData);
    },
    generateBill:async(postData)=>{
        return await apiClient.post('/generatebill', postData);
    },
    // 获取对账单列表
    getBills:async(postData)=>{
        return await apiClient.post('/getbills', postData);
    },
    // 结算对账单
    settleBill:async(postData)=>{
        return await apiClient.post('/settlebill', postData);
    },
    // 删除对账单
    deleteBill:async(postData)=>{
        return await apiClient.post('/deletebill', postData);
    },    
    // 保存探访基础信息
    saveInterviewBasicInfo:async(postData)=>{
        return await apiClient.post('/saveinterviewbasicinfo', postData);
    },
    // 获取探访基础信息
    getInterviewBasicInfo:async(postData)=>{
        return await apiClient.post('/getinterviewbasicinfo', postData);
    },
    // 添加探访记录
    addInterviewRecord:async(postData)=>{
        return await apiClient.post('/addinterviewrecord', postData);
    },
    saveInterviewServiceItem:async(postData)=>{
        return await apiClient.post('/saveinterviewserviceitem', postData);
    },
    // 获取探访服务项目数据
    getInterviewServiceItems:async(interviewNo)=>{
        return await apiClient.get(`/getinterviewserviceitems?interview_no=${interviewNo}`);
    },
    // 获取探访记录列表
    getInterviewRecords:async(params)=>{
        return await apiClient.post('/getinterviewrecords', params);
    },
    
    // Banner管理相关接口
    getBanners:async(postData)=>{
        return await apiClient.post('/getbanners', postData);
    },
    addBanner:async(postData)=>{
        return await apiClient.post('/addbanner', postData);
    },
    updateBanner:async(postData)=>{
        return await apiClient.post('/updatebanner', postData);
    },
    deleteBanner:async(postData)=>{
        return await apiClient.post('/deletebanner', postData);
    },

    // 商城首页专区Tab（推荐商品 / 重阳节活动）
    getMallZoneTabsAdmin:async(postData)=>{
        return await apiClient.post('/mallzonetabs/admin-list', postData || {});
    },
    saveMallZoneTab:async(postData)=>{
        return await apiClient.post('/mallzonetabs/save', postData);
    },
    updateMallZoneTabStatus:async(postData)=>{
        return await apiClient.post('/mallzonetabs/status', postData);
    },
    // 某商品分类下各商品的规格项（用于配置规格级领取规则）
    getMallZoneTabCategorySkus:async(postData)=>{
        return await apiClient.post('/mallzonetabs/category-skus', postData || {});
    },

    // 小程序首页-更多服务管理相关接口
    getHomeMoreServices:async(postData)=>{
        return await apiClient.post('/gethomemoreservices', postData);
    },
    addHomeMoreService:async(postData)=>{
        return await apiClient.post('/addhomemoreservice', postData);
    },
    updateHomeMoreService:async(postData)=>{
        return await apiClient.post('/updatehomemoreservice', postData);
    },
    deleteHomeMoreService:async(postData)=>{
        return await apiClient.post('/deletehomemoreservice', postData);
    },

    // 微信群码管理相关接口
    getWeixinQunList:async(postData)=>{
        return await apiClient.post('/getweixinqunlist', postData);
    },
    addWeixinQun:async(postData)=>{
        return await apiClient.post('/addweixinqun', postData);
    },
    updateWeixinQun:async(postData)=>{
        return await apiClient.post('/updateweixinqun', postData);
    },
    deleteWeixinQun:async(postData)=>{
        return await apiClient.post('/deleteweixinqun', postData);
    },
    updateWeixinQunSort:async(postData)=>{
        return await apiClient.post('/updateweixinqunsort', postData);
    },

    // ============================================================
    // 七助服务管理接口
    // ============================================================

    // 服务管理
    getServices: async(params) => {
        return await apiClient.get('/services', { params });
    },
    getServiceDetail: async(serviceId) => {
        return await apiClient.get(`/services/${serviceId}`);
    },
    createService: async(postData) => {
        return await apiClient.post('/services', postData);
    },
    updateService: async(serviceId, postData) => {
        return await apiClient.put(`/services/${serviceId}`, postData);
    },
    updateServiceStatus: async(serviceId, postData) => {
        return await apiClient.put(`/services/${serviceId}/status`, postData);
    },
    deleteService: async(serviceId) => {
        return await apiClient.delete(`/services/${serviceId}`);
    },

    // 服务分类管理
    getServiceTypes: async() => {
        return await apiClient.get('/service-types');
    },
    getServiceType: async(typeId) => {
        return await apiClient.get(`/service-types/${typeId}`);
    },
    createServiceType: async(postData) => {
        return await apiClient.post('/service-types', postData);
    },
    updateServiceType: async(typeId, postData) => {
        return await apiClient.put(`/service-types/${typeId}`, postData);
    },
    deleteServiceType: async(typeId) => {
        return await apiClient.delete(`/service-types/${typeId}`);
    },

    // 服务SKU表管理
    getServiceSkuTables: async(params) => {
        return await apiClient.get('/service-sku-tables', { params });
    },
    getServiceSkuTable: async(tableId) => {
        return await apiClient.get(`/service-sku-tables/${tableId}`);
    },
    createServiceSkuTable: async(postData) => {
        return await apiClient.post('/service-sku-tables', postData);
    },
    updateServiceSkuTable: async(tableId, postData) => {
        return await apiClient.put(`/service-sku-tables/${tableId}`, postData);
    },
    deleteServiceSkuTable: async(tableId) => {
        return await apiClient.delete(`/service-sku-tables/${tableId}`);
    },

    // SKU项管理
    getServiceSkuItems: async(params) => {
        return await apiClient.get('/service-sku-items', { params });
    },
    createServiceSkuItem: async(postData) => {
        return await apiClient.post('/service-sku-items', postData);
    },
    updateServiceSkuItem: async(itemId, postData) => {
        return await apiClient.put(`/service-sku-items/${itemId}`, postData);
    },
    deleteServiceSkuItem: async(itemId) => {
        return await apiClient.delete(`/service-sku-items/${itemId}`);
    },

    // 商品SKU表管理（商城商品规格，绑定商品）
    getProductSkuTables: async(params) => {
        return await apiClient.get('/product-sku-tables', { params });
    },
    getProductSkuTable: async(tableId) => {
        return await apiClient.get(`/product-sku-tables/${tableId}`);
    },
    createProductSkuTable: async(postData) => {
        return await apiClient.post('/product-sku-tables', postData);
    },
    updateProductSkuTable: async(tableId, postData) => {
        return await apiClient.put(`/product-sku-tables/${tableId}`, postData);
    },
    deleteProductSkuTable: async(tableId) => {
        return await apiClient.delete(`/product-sku-tables/${tableId}`);
    },
    // 获取商品全部SKU表及SKU项（含规格项价格），用于活动逐SKU定价
    getProductSkuTablesWithItems: async(productId) => {
        return await apiClient.get(`/products/${productId}/sku-tables`);
    },

    // 商品SKU项管理
    getProductSkuItems: async(params) => {
        return await apiClient.get('/product-sku-items', { params });
    },
    createProductSkuItem: async(postData) => {
        return await apiClient.post('/product-sku-items', postData);
    },
    updateProductSkuItem: async(itemId, postData) => {
        return await apiClient.put(`/product-sku-items/${itemId}`, postData);
    },
    deleteProductSkuItem: async(itemId) => {
        return await apiClient.delete(`/product-sku-items/${itemId}`);
    },

    // 会员层级管理
    getMembershipTiers: async() => {
        return await apiClient.get('/membership-tiers');
    },
    getMembershipTier: async(tierId) => {
        return await apiClient.get(`/membership-tiers/${tierId}`);
    },
    createMembershipTier: async(postData) => {
        return await apiClient.post('/membership-tiers', postData);
    },
    updateMembershipTier: async(tierId, postData) => {
        return await apiClient.put(`/membership-tiers/${tierId}`, postData);
    },

    // 用户会员管理
    getUserMemberships: async(params) => {
        return await apiClient.get('/user-memberships', { params });
    },
    getUserMembership: async(membershipId) => {
        return await apiClient.get(`/user-memberships/${membershipId}`);
    },
    createUserMembership: async(postData) => {
        return await apiClient.post('/user-memberships', postData);
    },
    updateUserMembership: async(membershipId, postData) => {
        return await apiClient.put(`/user-memberships/${membershipId}`, postData);
    },
    renewMembership: async(membershipId, postData) => {
        return await apiClient.post(`/user-memberships/${membershipId}/renew`, postData);
    },
    upgradeMembership: async(membershipId, postData) => {
        return await apiClient.post(`/user-memberships/${membershipId}/upgrade`, postData);
    },
    downgradeMembership: async(membershipId, postData) => {
        return await apiClient.post(`/user-memberships/${membershipId}/downgrade`, postData);
    },
    // 变更会员（升级：现金/测试；降级：生成审批，测试/未支付直接生效）
    changeMembership: async(membershipId, postData) => {
        return await apiClient.post(`/user-memberships/${membershipId}/change`, postData);
    },
    // 降级可退金额试算
    getMembershipDowngradeQuote: async(membershipId) => {
        return await apiClient.get(`/user-memberships/${membershipId}/downgrade-quote`);
    },
    // 会员变更审批
    getMembershipChangeRequests: async(params) => {
        return await apiClient.get('/membership-change-requests', { params });
    },
    approveMembershipChange: async(requestId, postData) => {
        return await apiClient.post(`/membership-change-requests/${requestId}/approve`, postData || {});
    },
    rejectMembershipChange: async(requestId, postData) => {
        return await apiClient.post(`/membership-change-requests/${requestId}/reject`, postData || {});
    },

    // 会员权益管理
    getMembershipBenefits: async(params) => {
        return await apiClient.get('/membership-benefits', { params });
    },
    getMembershipBenefit: async(benefitId) => {
        return await apiClient.get(`/membership-benefits/${benefitId}`);
    },
    createMembershipBenefit: async(postData) => {
        return await apiClient.post('/membership-benefits', postData);
    },
    updateMembershipBenefit: async(benefitId, postData) => {
        return await apiClient.put(`/membership-benefits/${benefitId}`, postData);
    },
    deleteMembershipBenefit: async(benefitId) => {
        return await apiClient.delete(`/membership-benefits/${benefitId}`);
    },
    getMembershipPurchases: async(params) => {
        return await apiClient.get('/membership-purchases', { params });
    },
    // 会员订单现金支付（后台）：未支付的会员升级/续费订单改为现金收款并开通/续费
    payMembershipPurchaseCash: async(postData) => {
        return await apiClient.post('/membership-purchases/pay-cash', postData);
    },
    // 取消待支付的会员订单（微信会员单 / 管家代办单）
    cancelMembershipOrder: async(postData) => {
        return await apiClient.post('/membership-orders/cancel', postData);
    },

    // 管家管理
    getButlers: async() => {
        return await apiClient.get('/butlers');
    },
    createButler: async(postData) => {
        return await apiClient.post('/butlers', postData);
    },
    updateButler: async(butlerId, postData) => {
        return await apiClient.put(`/butlers/${butlerId}`, postData);
    },
    setDefaultButler: async(butlerId) => {
        return await apiClient.put(`/butlers/${butlerId}/set-default`);
    },
    deleteButler: async(butlerId) => {
        return await apiClient.delete(`/butlers/${butlerId}`);
    },
    // 候选「专属管家」：users 表中 role=1 的管家用户（与 butlers 表/服务站无关）
    getDedicatedButlers: async(params) => {
        return await apiClient.get('/dedicated-butlers', { params });
    },
    // ========== 总后台「管家管理」 ==========
    getButlerAdminList: async(params) => {
        return await apiClient.get('/butler-admin/list', { params });
    },
    getButlerAdminOptions: async() => {
        return await apiClient.get('/butler-admin/options');
    },
    searchButlerCandidates: async(params) => {
        return await apiClient.get('/butler-admin/user-search', { params });
    },
    getButlerAdminDetail: async(butlerUserId) => {
        return await apiClient.get(`/butler-admin/butlers/${butlerUserId}`);
    },
    createButlerAdmin: async(postData) => {
        return await apiClient.post('/butler-admin/butlers', postData);
    },
    updateButlerAdmin: async(butlerUserId, postData) => {
        return await apiClient.put(`/butler-admin/butlers/${butlerUserId}`, postData);
    },
    setButlerAdminStatus: async(butlerUserId, status) => {
        return await apiClient.post(`/butler-admin/butlers/${butlerUserId}/status`, { status });
    },
    getButlerAuditLogs: async(params) => {
        return await apiClient.get('/butler-admin/audit-logs', { params });
    },
    getButlerAdminPerformance: async(butlerUserId, params) => {
        return await apiClient.get(`/butler-admin/${butlerUserId}/performance`, { params });
    },
    // 二期：名下客户 / 名下订单 / 收益情况 / 跟访服务记录
    getButlerAdminOptionsSimple: async() => {
        return await apiClient.get('/butler-admin/butler-options');
    },
    getButlerAdminActions: async() => {
        return await apiClient.get('/butler-admin/actions');
    },
    getButlerCustomerDemand: async(params) => {
        return await apiClient.get('/butler-admin/customer-demand', { params });
    },
    // 客户需求信息（上门服务登记表）：新增 / 更新
    createButlerDemand: async (postData) => {
        return await apiClient.post('/visit/records', postData);
    },
    updateButlerDemand: async (id, postData) => {
        return await apiClient.put('/visit/records/' + id, postData);
    },
    getButlerAdminCustomerList: async(params) => {
        return await apiClient.get('/butler-admin/customers', { params });
    },
    exportButlerAdminCustomers: async(postData) => {
        return await apiClient.post('/butler-admin/customers', { ...postData, export: 1 });
    },
    getButlerAdminOrders: async(params) => {
        return await apiClient.get('/butler-admin/orders', { params });
    },
    exportButlerAdminOrders: async(postData) => {
        return await apiClient.post('/butler-admin/orders', { ...postData, export: 1 });
    },
    getButlerAdminIncome: async(params) => {
        return await apiClient.get('/butler-admin/income', { params });
    },
    exportButlerAdminIncome: async(postData) => {
        // 导出 Excel：总表 + 每个管家一张明细工作表
        return await apiClient.post('/butler-admin/income', { ...postData, export: 1 }, { responseType: 'blob' });
    },
    payButlerAdminIncome: async(data) => {
        return await apiClient.post('/butler-admin/income/pay', data);
    },
    getButlerAdminFollowRecords: async(params) => {
        return await apiClient.get('/butler-admin/follow-records', { params });
    },
    getButlerAdminFollowStats: async(params) => {
        return await apiClient.get('/butler-admin/follow-stats', { params });
    },
    // 跟进统计表（管家维度，按管家汇总所选区间）
    getButlerAdminFollowStatsButler: async(params) => {
        return await apiClient.get('/butler-admin/follow-stats-butler', { params });
    },
    exportButlerAdminFollowRecords: async(postData) => {
        return await apiClient.post('/butler-admin/follow-records', { ...postData, export: 1 });
    },
    getButlerAdminRegisterConsents: async(params) => {
        return await apiClient.get('/butler-admin/register-consents', { params });
    },
    exportButlerAdminRegisterConsents: async(postData) => {
        return await apiClient.post('/butler-admin/register-consents', { ...postData, export: 1 });
    },
    // 公海用户（批量导入）
    getButlerPublicUsers: async(params) => {
        return await apiClient.get('/butler-admin/public-users', { params });
    },
    importButlerPublicUsers: async(postData) => {
        return await apiClient.post('/butler-admin/public-users/import', postData);
    },
    deleteButlerPublicUser: async(id) => {
        return await apiClient.delete(`/butler-admin/public-users/${id}`);
    },
    assignUserButler: async(membershipId, postData) => {
        return await apiClient.put(`/user-memberships/${membershipId}/assign-butler`, postData);
    },
    getEmergencyButlerPhone: async(userId) => {
        return await apiClient.get('/emergency/butler-phone', { params: { user_id: userId } });
    },

    // 服务范围模板管理
    getScopeTemplates: async() => {
        return await apiClient.get('/service-scope-templates');
    },
    getScopeTemplateDetail: async(templateId) => {
        return await apiClient.get(`/service-scope-templates/${templateId}`);
    },
    createScopeTemplate: async(postData) => {
        return await apiClient.post('/service-scope-templates', postData);
    },
    updateScopeTemplate: async(templateId, postData) => {
        return await apiClient.put(`/service-scope-templates/${templateId}`, postData);
    },
    deleteScopeTemplate: async(templateId) => {
        return await apiClient.delete(`/service-scope-templates/${templateId}`);
    },
    toggleScopeTemplateStatus: async(templateId, postData) => {
        return await apiClient.put(`/service-scope-templates/${templateId}/status`, postData);
    },
    copyScopeTemplate: async(templateId) => {
        return await apiClient.post(`/service-scope-templates/${templateId}/copy`);
    },
    assignServiceScope: async(serviceId, postData) => {
        return await apiClient.put(`/services/${serviceId}/scope`, postData);
    },
    getServicesScopeList: async() => {
        return await apiClient.get('/services-scope-list');
    },

    // 服务订单管理
    getServiceOrders: async(params) => {
        return await apiClient.get('/service-orders', { params });
    },
    getServiceOrdersStats: async(params) => {
        return await apiClient.get('/service-orders/stats', { params });
    },
    getServiceOrder: async(orderId) => {
        return await apiClient.get(`/service-orders/${orderId}`);
    },
    createServiceOrder: async(postData) => {
        return await apiClient.post('/service-orders', postData);
    },
    updateServiceOrderStatus: async(orderId, postData) => {
        return await apiClient.put(`/service-orders/${orderId}/status`, postData);
    },
    updateServiceOrder: async(orderId, postData) => {
        return await apiClient.put(`/service-orders/${orderId}`, postData);
    },
    assignServiceOrder: async(orderId, postData) => {
        return await apiClient.post(`/service-orders/${orderId}/assign`, postData);
    },
    reviewServiceOrder: async(orderId) => {
        return await apiClient.post(`/service-orders/${orderId}/review`);
    },
    // 接单：待审核 → 审核中（仅变更状态，脱离待审核1小时超时口径）
    acceptServiceOrderReview: async(orderId) => {
        return await apiClient.post(`/service-orders/${orderId}/accept`);
    },
    completeServiceOrder: async(orderId, postData) => {
        return await apiClient.post(`/service-orders/${orderId}/complete`, postData);
    },
    // 批量删除服务订单（仅已关闭订单可删除，deleteWorkOrders 为 true 时连同关联工单一起删除）
    batchDeleteServiceOrders: async(orderIds, deleteWorkOrders = false) => {
        return await apiClient.post('/service-orders/batch-delete', {
            order_ids: orderIds,
            delete_work_orders: !!deleteWorkOrders
        });
    },
    getOrderOperationLogs: async(orderNo) => {
        return await apiClient.get('/order-operation-logs', { params: { order_no: orderNo } });
    },

    // 服务商管理
    getServiceProviders: async() => {
        return await apiClient.get('/service-providers');
    },
    getServiceProvider: async(providerId) => {
        return await apiClient.get(`/service-providers/${providerId}`);
    },
    createServiceProvider: async(postData) => {
        return await apiClient.post('/service-providers', postData);
    },
    updateServiceProvider: async(providerId, postData) => {
        return await apiClient.put(`/service-providers/${providerId}`, postData);
    },
    deleteServiceProvider: async(providerId) => {
        return await apiClient.delete(`/service-providers/${providerId}`);
    },

    // 服务人员管理
    getServiceStaff: async(params) => {
        return await apiClient.get('/service-staff', { params });
    },
    getServiceStaffDetail: async(staffId) => {
        return await apiClient.get(`/service-staff/${staffId}`);
    },
    createServiceStaff: async(postData) => {
        return await apiClient.post('/service-staff', { 'postData': postData });
    },
    updateServiceStaff: async(staffId, postData) => {
        return await apiClient.put(`/service-staff/${staffId}`, { 'postData': postData });
    },
    toggleStaffDuty: async(staffId) => {
        return await apiClient.post(`/service-staff/${staffId}/toggle-duty`);
    },
    deleteServiceStaff: async(staffId) => {
        return await apiClient.delete(`/service-staff/${staffId}`);
    },

    // 工单管理
    getWorkOrders: async(params) => {
        return await apiClient.get('/work-orders', { params });
    },
    getWorkOrder: async(workOrderId) => {
        return await apiClient.get(`/work-orders/${workOrderId}`);
    },
    createWorkOrder: async(postData) => {
        return await apiClient.post('/work-orders', postData);
    },
    assignWorkOrder: async(workOrderId, postData) => {
        return await apiClient.post(`/work-orders/${workOrderId}/assign`, postData);
    },
    acceptWorkOrder: async(workOrderId) => {
        return await apiClient.post(`/work-orders/${workOrderId}/accept`);
    },
    startWorkOrder: async(workOrderId, postData) => {
        return await apiClient.post(`/work-orders/${workOrderId}/start`, postData);
    },
    completeWorkOrder: async(workOrderId, postData) => {
        return await apiClient.post(`/work-orders/${workOrderId}/complete`, postData);
    },
    // 进度弹窗内追加开始服务/完成服务照片（不改变工单状态）
    addWorkOrderStartPhoto: async(workOrderId, photoUrl) => {
        return await apiClient.post(`/work-orders/${workOrderId}/start-photo`, { photo_url: photoUrl });
    },
    addWorkOrderCompletionPhoto: async(workOrderId, photoUrl) => {
        return await apiClient.post(`/work-orders/${workOrderId}/completion-photo`, { photo_url: photoUrl });
    },
    transferWorkOrder: async(workOrderId, postData) => {
        return await apiClient.post(`/work-orders/${workOrderId}/transfer`, postData);
    },
    markAbnormalWorkOrder: async(workOrderId, postData) => {
        return await apiClient.post(`/work-orders/${workOrderId}/mark-abnormal`, postData);
    },
    batchConfirmWorkOrders: async(postData) => {
        return await apiClient.post('/work-orders/batch-confirm', postData);
    },

    // 服务配额管理
    getServiceQuota: async(params) => {
        return await apiClient.get('/service-quota', { params });
    },
    getServiceQuotaDetail: async(quotaId) => {
        return await apiClient.get(`/service-quota/${quotaId}`);
    },
    resetServiceQuota: async(quotaId) => {
        return await apiClient.post(`/service-quota/${quotaId}/reset`);
    },
    updateServiceQuota: async(quotaId, postData) => {
        return await apiClient.put(`/service-quota/${quotaId}`, postData);
    },

    // 配额审计日志
    getQuotaAuditLog: async(params) => {
        return await apiClient.get('/quota-audit-log', { params });
    },

    // 优惠券管理
    getCoupons: async(params) => {
        return await apiClient.get('/coupons', { params });
    },
    createCoupon: async(postData) => {
        return await apiClient.post('/coupons', postData);
    },
    updateCoupon: async(couponId, postData) => {
        return await apiClient.put(`/coupons/${couponId}`, postData);
    },
    updateCouponStatus: async(couponId, postData) => {
        return await apiClient.put(`/coupons/${couponId}/status`, postData);
    },
    getServiceCoupons: async(params) => {
        return await apiClient.get('/coupons', { params });
    },
    createServiceCoupon: async(postData) => {
        return await apiClient.post('/coupons', postData);
    },
    updateServiceCoupon: async(couponId, postData) => {
        return await apiClient.put(`/coupons/${couponId}`, postData);
    },
    updateServiceCouponStatus: async(couponId, postData) => {
        return await apiClient.put(`/coupons/${couponId}/status`, postData);
    },

    // 推荐管理
    getReferrals: async(params) => {
        return await apiClient.get('/referrals', { params });
    },
    grantReferralReward: async(referralId) => {
        return await apiClient.post(`/referrals/${referralId}/reward`);
    },
    payReferralReward: async(referralId) => {
        return await apiClient.post(`/referrals/${referralId}/reward`);
    },

    // ========== 图片上传相关 ==========
    // 获取上传地址
    getUploadUrl: () => {
        if (process.env.NODE_ENV === 'development') {
            return 'http://localhost:5000/upload';
        }
        return '/upload';
    },
    
    // 上传文件（支持FormData）
    uploadFile: async (formData) => {
        try {
            // 根据环境选择正确的上传地址
            let uploadUrl;
            if (process.env.NODE_ENV === 'development') {
                // 开发环境：直接发送到后端服务器
                uploadUrl = 'http://localhost:5000/upload';
            } else {
                // 生产环境：通过代理发送到后端
                uploadUrl = '/upload';
            }
            
            console.log('上传地址:', uploadUrl);
            
            const response = await axios.post(uploadUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 30000
            });
            return response;
        } catch (error) {
            console.error('上传文件失败:', error);
            // 增强错误信息
            if (error.response) {
                console.error('服务器响应:', error.response.status, error.response.data);
            } else if (error.request) {
                console.error('请求已发送但没有收到响应');
            }
            throw error;
        }
    },

    getReferralStats: async() => {
        return await apiClient.get('/referrals/stats');
    },

    // 钻享方案管理
    getDiamondPlans: async(params) => {
        return await apiClient.get('/diamond-plans', { params });
    },
    createDiamondPlan: async(postData) => {
        return await apiClient.post('/diamond-plans', postData);
    },
    updateDiamondPlan: async(planId, postData) => {
        return await apiClient.put(`/diamond-plans/${planId}`, postData);
    },
    updateDiamondPlanStatus: async(planId, postData) => {
        return await apiClient.put(`/diamond-plans/${planId}/status`, postData);
    },

    // 数据统计
    getServiceStatistics: async() => {
        return await apiClient.get('/service-statistics');
    },
    getServiceReports: async(params) => {
        return await apiClient.get('/service-reports', { params });
    },

    // 活动报名管理
    getSignupActivities: async(postData) => {
        return await apiClient.post('/get-signup-activities', postData);
    },
    getArticleSignups: async(postData) => {
        return await apiClient.post('/get-article-signups', postData);
    },
    updateSignupStatus: async(postData) => {
        return await apiClient.post('/update-signup-status', postData);
    },

    // 签到统计
    getCheckinStats: async(postData) => {
        return await apiClient.post('/get-checkin-stats', postData || {});
    },

    // 文章评论管理
    getArticleComments: async(postData) => {
        return await apiClient.post('/get-article-comments', postData);
    },
    updateCommentStatus: async(postData) => {
        return await apiClient.post('/update-comment-status', postData);
    },
    updateCommentTop: async(postData) => {
        return await apiClient.post('/update-comment-top', postData);
    },
    deleteComment: async(postData) => {
        return await apiClient.post('/delete-comment', postData);
    },

    // AI聊天记录
    getAiChatRecords: async(postData) => {
        return await apiClient.post('/get-ai-chat-records', postData || {});
    },

    // 批量删除AI聊天记录
    deleteAiChatRecords: async(ids) => {
        return await apiClient.post('/delete-ai-chat-records', { ids });
    },

    // 健康档案管理
    getHealthRecords: async(postData) => {
        return await apiClient.post('/health-records', postData || {});
    },
    addHealthRecord: async(postData) => {
        return await apiClient.post('/health-record/add', postData);
    },
    updateHealthRecord: async(postData) => {
        return await apiClient.post('/health-record/update', postData);
    },
    deleteHealthRecord: async(postData) => {
        return await apiClient.post('/health-record/delete', postData);
    },
    searchHealthUsers: async(postData) => {
        return await apiClient.post('/health-record/search-users', postData);
    },

    // 优惠方案管理
    getDiscountPlans: async(params) => {
        return await apiClient.get('/discount-plans', { params });
    },
    getDiscountPlan: async(planId) => {
        return await apiClient.get(`/discount-plans/${planId}`);
    },
    createDiscountPlan: async(postData) => {
        return await apiClient.post('/discount-plans', postData);
    },
    updateDiscountPlan: async(planId, postData) => {
        return await apiClient.put(`/discount-plans/${planId}`, postData);
    },
    deleteDiscountPlan: async(planId) => {
        return await apiClient.delete(`/discount-plans/${planId}`);
    },
    getAllSkuItems: async(params) => {
        return await apiClient.get('/sku-items/all', { params });
    },
    getSkuTablesWithItems: async() => {
        return await apiClient.get('/sku-tables-with-items');
    },

    // 匹配优惠方案（根据SKU项ID）
    matchDiscountPlans: async(skuItemIds) => {
        return await apiClient.get('/discount-plans/match', {
            params: { sku_item_ids: skuItemIds.join(',') }
        });
    },

    // 获取服务SKU表及SKU项（小程序端接口，返回完整的表和项）
    getServiceSkuTablesWithItems: async(serviceId) => {
        return await apiClient.get(`/services/${serviceId}/sku-tables`);
    },

    // 获取用户当前会员等级
    getUserCurrentMembership: async(userId) => {
        return await apiClient.get('/user/current-membership', {
            params: { user_id: userId }
        });
    },

    // 超时日志
    getTimeoutLogs: async(params) => {
        return await apiClient.get('/order-timeout-logs', { params });
    },
    // 紧急呼叫记录
    getEmergencyCallRecords: async(params) => {
        return await apiClient.get('/emergency/call-records', { params });
    },
    // 违约记录
    getProviderBreaches: async(params) => {
        return await apiClient.get('/provider-breaches', { params });
    },
    // 补偿记录
    getCompensations: async(params) => {
        return await apiClient.get('/compensations', { params });
    },

    // ========== 扫码用餐活动管理 ==========
    getMealActivities: async() => {
        return await apiClient.get('/meal-activities');
    },
    getMealActivity: async(activityId) => {
        return await apiClient.get(`/meal-activities/${activityId}`);
    },
    // 活动白名单：符合条件的用户列表
    getMealActivityWhitelist: async(activityId, params) => {
        return await apiClient.get(`/meal-activities/${activityId}/whitelist`, { params });
    },
    // 活动参与记录：该活动的下单记录列表
    getMealActivityRecords: async(activityId, params) => {
        return await apiClient.get(`/meal-activities/${activityId}/records`, { params });
    },
    // 批量导入白名单（Excel 由前端解析后提交 users + area + address）
    importMealActivityWhitelist: async(activityId, postData) => {
        return await apiClient.post(`/meal-activities/${activityId}/whitelist/import`, postData);
    },
    // 移出白名单（仅把 users.shop_id 重置为 0，不删除用户）
    removeMealActivityWhitelist: async(activityId, postData) => {
        return await apiClient.post(`/meal-activities/${activityId}/whitelist/remove`, postData);
    },
    createMealActivity: async(postData) => {
        return await apiClient.post('/meal-activities', postData);
    },
    updateMealActivity: async(activityId, postData) => {
        return await apiClient.put(`/meal-activities/${activityId}`, postData);
    },
    deleteMealActivity: async(activityId) => {
        return await apiClient.delete(`/meal-activities/${activityId}`);
    },
    updateMealActivityStatus: async(activityId, postData) => {
        return await apiClient.put(`/meal-activities/${activityId}/status`, postData);
    },
    getMealQr: async(activityId) => {
        return await apiClient.get('/get-meal-qr', {
            params: { activity_id: activityId },
            responseType: 'blob'
        });
    },
    // 商品分享小程序码（扫码进入商品详情页）
    getProductQr: async(productId) => {
        return await apiClient.get('/get-product-qr', {
            params: { product_id: productId },
            responseType: 'blob'
        });
    },

    // ========== 管家上门服务信息登记 ==========
    // 管理端高级查询：按用户姓名 / 管家姓名查询登记记录（含全部跟进记录）
    getVisitAdminRecords: async(params) => {
        return await apiClient.get('/visit/admin-records', { params });
    },
    // 更新跟进记录
    updateVisitFollowUp: async(followId, postData) => {
        return await apiClient.put(`/visit/follow-ups/${followId}`, postData);
    },
    // 获取管家列表（role=1 的用户）
    getVisitButlers: async() => {
        return await apiClient.get('/visit/butlers');
    },
    // 更新登记记录（用于分配管家等）
    updateVisitRecord: async(recordId, postData) => {
        return await apiClient.put(`/visit/records/${recordId}`, postData);
    },

    // ========== 小程序首页跑马灯通知管理 ==========
    getHomeNoticesAdmin: async(params) => {
        return await apiClient.get('/home-notices/admin', { params });
    },
    getHomeNoticeCommunities: async() => {
        return await apiClient.get('/home-notices/communities');
    },
    createHomeNotice: async(postData) => {
        return await apiClient.post('/home-notices', postData);
    },
    updateHomeNotice: async(noticeId, postData) => {
        return await apiClient.put(`/home-notices/${noticeId}`, postData);
    },
    updateHomeNoticeStatus: async(noticeId, postData) => {
        return await apiClient.put(`/home-notices/${noticeId}/status`, postData);
    },
    deleteHomeNotice: async(noticeId) => {
        return await apiClient.delete(`/home-notices/${noticeId}`);
    },

    // ========== 青银悦社 API ==========
    // 数据看板
    getQyysStats: async () => {
        return await apiClient.get('/qyys/admin/stats');
    },
    // 文章管理（发布时后端强制校验金融/健康免责声明）
    getQyysArticles: async (params) => {
        return await apiClient.get('/qyys/admin/articles', { params });
    },
    getQyysArticle: async (id) => {
        return await apiClient.get(`/qyys/admin/articles/${id}`);
    },
    createQyysArticle: async (data) => {
        return await apiClient.post('/qyys/admin/articles', data);
    },
    updateQyysArticle: async (id, data) => {
        return await apiClient.put(`/qyys/admin/articles/${id}`, data);
    },
    deleteQyysArticle: async (id) => {
        return await apiClient.delete(`/qyys/admin/articles/${id}`);
    },
    // 话题分类（原五大支柱，后台可维护名称/图标/配色/排序/显隐）
    getQyysArticlePillars: async () => {
        return await apiClient.get('/qyys/admin/article-pillars');
    },
    createQyysArticlePillar: async (data) => {
        return await apiClient.post('/qyys/admin/article-pillars', data);
    },
    updateQyysArticlePillar: async (id, data) => {
        return await apiClient.put(`/qyys/admin/article-pillars/${id}`, data);
    },
    deleteQyysArticlePillar: async (id) => {
        return await apiClient.delete(`/qyys/admin/article-pillars/${id}`);
    },
    // 评论管理
    getQyysArticleComments: async (params) => {
        return await apiClient.get('/qyys/admin/article-comments', { params });
    },
    updateQyysArticleComment: async (id, data) => {
        return await apiClient.put(`/qyys/admin/article-comments/${id}`, data);
    },
    deleteQyysArticleComment: async (id) => {
        return await apiClient.delete(`/qyys/admin/article-comments/${id}`);
    },
    // 圈子管理
    getQyysCircles: async () => {
        return await apiClient.get('/qyys/admin/circles');
    },
    createQyysCircle: async (data) => {
        return await apiClient.post('/qyys/admin/circles', data);
    },
    updateQyysCircle: async (id, data) => {
        return await apiClient.put(`/qyys/admin/circles/${id}`, data);
    },
    deleteQyysCircle: async (id) => {
        return await apiClient.delete(`/qyys/admin/circles/${id}`);
    },
    saveQyysMonitor: async (data) => {
        return await apiClient.post('/qyys/admin/monitors', data);
    },
    // 圈子话题（圈子二级页「圈子话题」区块，按圈子维护）
    getQyysCircleTopics: async (params) => {
        return await apiClient.get('/qyys/admin/circle-topics', { params });
    },
    createQyysCircleTopic: async (data) => {
        return await apiClient.post('/qyys/admin/circle-topics', data);
    },
    updateQyysCircleTopic: async (id, data) => {
        return await apiClient.put(`/qyys/admin/circle-topics/${id}`, data);
    },
    deleteQyysCircleTopic: async (id) => {
        return await apiClient.delete(`/qyys/admin/circle-topics/${id}`);
    },
    // 往期活动回顾（圈子二级页「往期活动回顾」+ 回顾图文页）
    getQyysReviews: async (params) => {
        return await apiClient.get('/qyys/admin/reviews', { params });
    },
    getQyysReview: async (id) => {
        return await apiClient.get(`/qyys/admin/reviews/${id}`);
    },
    createQyysReview: async (data) => {
        return await apiClient.post('/qyys/admin/reviews', data);
    },
    updateQyysReview: async (id, data) => {
        return await apiClient.put(`/qyys/admin/reviews/${id}`, data);
    },
    deleteQyysReview: async (id) => {
        return await apiClient.delete(`/qyys/admin/reviews/${id}`);
    },
    // 活动/课件管理
    getQyysActivities: async (params) => {
        return await apiClient.get('/qyys/admin/activities', { params });
    },
    getQyysActivity: async (id) => {
        return await apiClient.get(`/qyys/admin/activities/${id}`);
    },
    createQyysActivity: async (data) => {
        return await apiClient.post('/qyys/admin/activities', data);
    },
    updateQyysActivity: async (id, data) => {
        return await apiClient.put(`/qyys/admin/activities/${id}`, data);
    },
    deleteQyysActivity: async (id) => {
        return await apiClient.delete(`/qyys/admin/activities/${id}`);
    },
    // 报名订单
    getQyysOrders: async (params) => {
        return await apiClient.get('/qyys/admin/orders', { params });
    },
    updateQyysOrderStatus: async (id, data) => {
        return await apiClient.put(`/qyys/admin/orders/${id}/status`, data);
    },
    updateQyysOrder: async (id, data) => {
        return await apiClient.put(`/qyys/admin/orders/${id}`, data);
    },
    // 老友日志审核（先审后发）
    getQyysPosts: async (params) => {
        return await apiClient.get('/qyys/admin/posts', { params });
    },
    auditQyysPost: async (id, data) => {
        return await apiClient.put(`/qyys/admin/posts/${id}/audit`, data);
    },
    deleteQyysPost: async (id) => {
        return await apiClient.delete(`/qyys/admin/posts/${id}`);
    },
    // 班长申请审核
    getQyysMonitorApplies: async (params) => {
        return await apiClient.get('/qyys/admin/monitor-applies', { params });
    },
    getQyysMonitorApply: async (id) => {
        return await apiClient.get(`/qyys/admin/monitor-applies/${id}`);
    },
    auditQyysMonitorApply: async (id, data) => {
        return await apiClient.put(`/qyys/admin/monitor-applies/${id}/audit`, data);
    },
    // 会员管理
    getQyysMembers: async (params) => {
        return await apiClient.get('/qyys/admin/members', { params });
    },
    updateQyysMember: async (id, data) => {
        return await apiClient.put(`/qyys/admin/members/${id}`, data);
    },
    // 圈子成员名单
    getQyysCircleMembers: async (params) => {
        return await apiClient.get('/qyys/admin/circle-members', { params });
    },
    updateQyysCircleMember: async (id, data) => {
        return await apiClient.put(`/qyys/admin/circle-members/${id}`, data);
    },
    deleteQyysCircleMember: async (id) => {
        return await apiClient.delete(`/qyys/admin/circle-members/${id}`);
    },
    // 举报处理（互动记录，合规要求可追溯）
    getQyysInteractions: async (params) => {
        return await apiClient.get('/qyys/admin/interactions', { params });
    },
    handleQyysInteraction: async (id, data) => {
        return await apiClient.put(`/qyys/admin/interactions/${id}/handle`, data);
    },
    deleteQyysInteraction: async (id) => {
        return await apiClient.delete(`/qyys/admin/interactions/${id}`);
    },
    // 简历门诊提交记录（运营跟进再就业）
    getQyysResumeSubmissions: async (params) => {
        return await apiClient.get('/qyys/admin/resume-submissions', { params });
    },
    updateQyysResumeSubmission: async (id, data) => {
        return await apiClient.put(`/qyys/admin/resume-submissions/${id}`, data);
    },
    // 首页配置（PageHeader / 周刊 Banner / 班长招募 / 工具区标题）
    getQyysConfigs: async (params) => {
        return await apiClient.get('/qyys/admin/configs', { params });
    },
    saveQyysConfig: async (configKey, data) => {
        return await apiClient.put(`/qyys/admin/configs/${configKey}`, data);
    },
    // 常用工具管理
    getQyysTools: async () => {
        return await apiClient.get('/qyys/admin/tools');
    },
    createQyysTool: async (data) => {
        return await apiClient.post('/qyys/admin/tools', data);
    },
    updateQyysTool: async (id, data) => {
        return await apiClient.put(`/qyys/admin/tools/${id}`, data);
    },
    deleteQyysTool: async (id) => {
        return await apiClient.delete(`/qyys/admin/tools/${id}`);
    },

    // 通用方法
    get: async(url, config) => {
        return await apiClient.get(url, config);
    },
    post: async(url, data, config) => {
        return await apiClient.post(url, data, config);
    },
    put: async(url, data, config) => {
        return await apiClient.put(url, data, config);
    },
    delete: async(url, config) => {
        return await apiClient.delete(url, config);
    }
}

export default API;

// 导出图片URL处理方法
export { getFullImageUrl };